import { colors } from "../theme/colors";

export const DIMENSION_WEIGHTS = {
    sportsmanship: 1.5,
    integrity: 2.0,
    communication: 1.0,
    teamPlay: 1.2,
    reliability: 1.3,
    reporterQuality: 0.5,
    profileCompleteness: 0.8,
};

const TOTAL_WEIGHT = Object.values(DIMENSION_WEIGHTS).reduce((sum, weight) => sum + weight, 0);

export function calculateOverallFromDimensionBaselines(dimensionBaselines = {}) {
    let weightedSum = 0;

    for (const [dimension, weight] of Object.entries(DIMENSION_WEIGHTS)) {
        const score = Number(dimensionBaselines[dimension]);
        if (Number.isFinite(score)) {
            weightedSum += score * weight;
        }
    }

    return Math.round(weightedSum / TOTAL_WEIGHT);
}

export const TREND_COLORS = {
    up: colors.state.success.main,
    down: colors.state.danger.main,
};

const DAY_MS = 24 * 60 * 60 * 1000;

function parseHistoryEvents(history) {
    return (history || [])
        .map((entry) => {
            const timestamp = new Date(entry.timestamp).getTime();
            const score = Number(entry.newOverallScore ?? entry.oldOverallScore);
            const previousScore = Number(entry.oldOverallScore);

            if (!Number.isFinite(timestamp) || !Number.isFinite(score)) {
                return null;
            }

            return { timestamp, score, previousScore };
        })
        .filter(Boolean)
        .sort((left, right) => left.timestamp - right.timestamp);
}

function getScoreAtTimestamp(events, timestamp, fallbackScore) {
    if (events.length > 0 && timestamp < events[0].timestamp) {
        if (Number.isFinite(events[0].previousScore)) {
            return events[0].previousScore;
        }
        return fallbackScore;
    }

    let score = fallbackScore;
    for (const event of events) {
        if (event.timestamp <= timestamp) {
            score = event.score;
        } else {
            break;
        }
    }

    return score;
}

export function getTrustScoreTrend30d(history, currentScore) {
    if (!Number.isFinite(currentScore)) {
        return { label: "Stable", delta: null, direction: "flat" };
    }

    const startTs = Date.now() - 30 * DAY_MS;
    const events = parseHistoryEvents(history);
    const scoreThen = getScoreAtTimestamp(events, startTs, currentScore);
    const delta = currentScore - scoreThen;

    if (delta > 0) {
        return { label: "Improving", delta, direction: "up" };
    }
    if (delta < 0) {
        return { label: "Declining", delta, direction: "down" };
    }

    return { label: "Stable", delta: 0, direction: "flat" };
}

export function formatTrustScoreTrendDelta(delta) {
    if (delta == null) {
        return "—";
    }

    return delta > 0 ? `+${delta}` : `${delta}`;
}

export function resolvePlatformOverallAverage(baselinesDoc) {
    const dimensionBaselines = resolveDimensionBaselinesMap(baselinesDoc);
    if (Object.keys(dimensionBaselines).length > 0) {
        return calculateOverallFromDimensionBaselines(dimensionBaselines);
    }

    const storedOverall = Number(baselinesDoc?.overallAverage);
    return Number.isFinite(storedOverall) ? storedOverall : 50;
}

export function resolveDimensionBaselinesMap(baselinesDoc) {
    if (!baselinesDoc || typeof baselinesDoc !== "object") {
        return {};
    }

    const nested = baselinesDoc.baselines;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }

    return baselinesDoc;
}

export function resolveDimensionBaseline(baselinesDoc, key, { fallback = null } = {}) {
    const value = Number(resolveDimensionBaselinesMap(baselinesDoc)?.[key]);
    if (Number.isFinite(value)) {
        return value;
    }

    return fallback;
}
